import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

interface AuthGuardProps {
  children: React.ReactNode;
  requiredRoles?: string | string[];
  requireAdminAccess?: boolean;
}

export const AuthGuard: React.FC<AuthGuardProps> = ({
  children,
  requiredRoles,
  requireAdminAccess = false,
}) => {
  const { isAuthenticated, user, isLoading, hasRole, hasAdminAccess } = useAuth();
  const location = useLocation();

  // Show loading spinner while checking auth
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Navigate
        to="/auth/login"
        state={{ from: location }}
        replace
      />
    );
  }

  // Check admin access requirement
  if (requireAdminAccess && !hasAdminAccess()) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">
            You don't have permission to access this page. Admin or Monitor role required.
          </p>
          <p className="text-sm text-gray-500">
            Current role: <span className="font-medium">{user?.role}</span>
          </p>
        </div>
      </div>
    );
  }

  // Check specific role requirements
  if (requiredRoles && !hasRole(requiredRoles)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Insufficient Permissions</h1>
          <p className="text-gray-600 mb-6">
            You don't have the required role to access this page.
          </p>
          <p className="text-sm text-gray-500">
            Required: <span className="font-medium">{Array.isArray(requiredRoles) ? requiredRoles.join(', ') : requiredRoles}</span>
          </p>
          <p className="text-sm text-gray-500">
            Current: <span className="font-medium">{user?.role}</span>
          </p>
        </div>
      </div>
    );
  }

  // All checks passed, render children
  return <>{children}</>;
};